import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { Sync } from "@mui/icons-material";
import { ConfigDropdownForm } from "@/pages/config/ConfigDropdownForm";
import { useNotification } from "@/context/NotificationContext";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import {
  getIntegracao,
  listLojas,
  listMapaIntegracao,
  listProvedoresIntegracao,
  lojaLabel,
  saveIntegracao,
  saveMapaIntegracao,
  sugerirMapaIntegracao,
  testarIntegracao,
  type Integracao,
  type Loja,
  type Provedor,
  type ResultadoTesteIntegracao,
} from "@/api/escala.service";
import {
  aplicarSugestoes,
  erroDoForm,
  formDaIntegracao,
  linhasDoMapa,
  mapaDasLinhas,
  MAX_REF_INTEGRACAO,
  paramsDoForm,
  senhaObrigatoria,
  textoTesteOk,
  type FormIntegracao,
  type LinhaMapa,
} from "@/utils/escalaIntegracao";

// Configuração da integração externa (S9b): credenciais do sistema de origem e o
// mapa loja ↔ empresa/filial dele. Nada aqui é específico de um sistema: o
// provedor, os rótulos e os campos extras vêm de listar_provedores_integracao.
// A senha é write-only — nunca volta do servidor, só `tem_senha`.
export function EscalaIntegracaoTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();

  const [provedores, setProvedores] = useState<Provedor[]>([]);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [gravada, setGravada] = useState<Integracao | null>(null);
  const [form, setForm] = useState<FormIntegracao | null>(null);
  const [linhas, setLinhas] = useState<LinhaMapa[]>([]);
  const [erroCarga, setErroCarga] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [salvandoMapa, setSalvandoMapa] = useState(false);
  const [testando, setTestando] = useState(false);
  const [sugerindo, setSugerindo] = useState(false);
  const [teste, setTeste] = useState<ResultadoTesteIntegracao | null>(null);

  useEffect(() => {
    let cancelado = false;
    setErroCarga(false);
    Promise.all([
      listProvedoresIntegracao(clientId),
      getIntegracao(clientId),
      listMapaIntegracao(clientId),
      listLojas(clientId),
    ])
      .then(([ps, integracao, mapa, todasLojas]) => {
        if (cancelado) return;
        const ativas = (todasLojas ?? []).filter((l) => l.ativo);
        setProvedores(ps ?? []);
        setGravada(integracao);
        setForm(formDaIntegracao(integracao, ps?.[0]?.id ?? ""));
        setLojas(ativas);
        setLinhas(linhasDoMapa(ativas.map((l) => l.id), mapa ?? []));
      })
      .catch(() => {
        if (cancelado) return;
        setErroCarga(true);
        notify("Erro ao carregar a integração externa", "error");
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (erroCarga) return <Alert severity="error">Não foi possível carregar a integração externa.</Alert>;
  if (!form || !gravada) return <Sync />;
  if (provedores.length === 0) {
    return <Alert severity="info">Nenhum sistema externo está disponível para integração.</Alert>;
  }

  const provedor = provedores.find((p) => p.id === form.provedor);
  const alterar = (parte: Partial<FormIntegracao>) => {
    setForm((f) => f && { ...f, ...parte });
    setTeste(null);
  };

  // As opções e a importação de carga são do provedor: trocar de sistema as zera.
  const trocarProvedor = (id: string) => alterar({ provedor: id, opcoes: {}, importar_carga_horaria: false });

  const salvar = async () => {
    const erro = erroDoForm(form, gravada, provedor);
    if (erro) return notify(erro, "error");

    setSalvando(true);
    try {
      const result = await saveIntegracao(paramsDoForm(form), clientId);
      setGravada(result);
      setForm(formDaIntegracao(result, form.provedor));
      setTeste(null);
      // Sem a senha: o log guarda só o que mudou de configuração.
      log(
        LogEventType.Updated,
        {
          provedor: result.provedor,
          ativa: result.ativa,
          sincronizar_diariamente: result.sincronizar_diariamente,
          importar_carga_horaria: result.importar_carga_horaria,
          url: result.url,
        },
        "escala_integracao",
        clientId,
        "info",
      );
      notify("Integração salva!", "success");
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar a integração", "error");
    } finally {
      setSalvando(false);
    }
  };

  // Testa o que está no formulário, sem salvar: dá para conferir antes de gravar.
  const testar = async () => {
    const erro = erroDoForm(form, gravada, provedor);
    if (erro) return notify(erro, "error");
    setTestando(true);
    try {
      setTeste(await testarIntegracao(paramsDoForm(form), clientId));
    } catch (e: any) {
      notify(e?.message || "Erro ao testar a conexão", "error");
    } finally {
      setTestando(false);
    }
  };

  const sugerir = async () => {
    setSugerindo(true);
    try {
      const sugestoes = await sugerirMapaIntegracao(clientId);
      const { linhas: novas, aplicadas } = aplicarSugestoes(linhas, sugestoes ?? []);
      setLinhas(novas);
      notify(
        aplicadas > 0
          ? `${aplicadas} loja(s) preenchida(s) pelo CNPJ. Confira e salve o mapa.`
          : "Nenhuma loja sem ligação tem o mesmo CNPJ de uma filial do sistema.",
        aplicadas > 0 ? "success" : "info",
      );
    } catch (e: any) {
      notify(e?.message || "Erro ao buscar sugestões", "error");
    } finally {
      setSugerindo(false);
    }
  };

  const salvarMapa = async () => {
    const { mapa, erro } = mapaDasLinhas(linhas);
    if (erro) return notify(erro, "error");
    setSalvandoMapa(true);
    try {
      const result = await saveMapaIntegracao(mapa, clientId);
      setLinhas(linhasDoMapa(lojas.map((l) => l.id), result ?? []));
      log(LogEventType.Updated, { lojas: mapa.length }, "escala_integracao_loja", clientId, "info");
      notify("Mapa de lojas salvo!", "success");
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar o mapa de lojas", "error");
    } finally {
      setSalvandoMapa(false);
    }
  };

  const rotuloEmpresa = provedor?.rotulo_empresa || "Empresa";
  const rotuloFilial = provedor?.rotulo_filial || "Filial";

  return (
    <>
      <ConfigDropdownForm
        title="Integração externa"
        subtitle="Importa colaboradores, afastamentos, férias e feriados de um sistema de RH. Vale para todas as lojas deste cliente."
        onSave={salvar}
        loading={salvando}
        defaultOpen
      >
        <TextField
          select
          label="Sistema"
          fullWidth
          required
          value={form.provedor}
          onChange={(e) => trocarProvedor(e.target.value)}
        >
          {provedores.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.nome}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="URL do serviço"
          fullWidth
          required
          value={form.url}
          onChange={(e) => alterar({ url: e.target.value })}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            label="Usuário"
            fullWidth
            required
            autoComplete="off"
            value={form.usuario}
            onChange={(e) => alterar({ usuario: e.target.value })}
          />
          <TextField
            label="Senha"
            type="password"
            fullWidth
            required={senhaObrigatoria(form, gravada)}
            autoComplete="new-password"
            value={form.senha}
            onChange={(e) => alterar({ senha: e.target.value })}
            helperText={gravada.tem_senha ? "Em branco mantém a senha gravada" : undefined}
          />
        </Stack>
        <TextField
          label="Tempo limite por consulta (segundos)"
          type="number"
          fullWidth
          required
          value={form.timeout_seg}
          onChange={(e) => alterar({ timeout_seg: Number(e.target.value) })}
          slotProps={{ htmlInput: { min: 1, max: 600, step: 1 } }}
        />
        {(provedor?.campos ?? []).map((c) => (
          <TextField
            key={c.chave}
            label={c.rotulo}
            fullWidth
            required={c.obrigatorio}
            value={form.opcoes[c.chave] ?? ""}
            onChange={(e) => alterar({ opcoes: { ...form.opcoes, [c.chave]: e.target.value } })}
          />
        ))}
        <FormControlLabel
          control={<Switch checked={form.ativa} onChange={(e) => alterar({ ativa: e.target.checked })} />}
          label="Integração ativa"
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.sincronizar_diariamente}
              disabled={!form.ativa}
              onChange={(e) => alterar({ sincronizar_diariamente: e.target.checked })}
            />
          }
          label="Sincronizar todo dia às 05:00"
        />
        {provedor?.carga_horaria && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.importar_carga_horaria}
                  onChange={(e) => alterar({ importar_carga_horaria: e.target.checked })}
                />
              }
              label="Importar carga horária"
            />
            <FormHelperText>
              A carga de quem vem de {provedor.nome} passa a seguir o horário de lá, de segunda a sábado, e muda o
              horário das escalas em rascunho. A carga que não existe é criada só com o que vem de lá: o domingo fica
              em branco quando lá não tem horário, e o feriado sempre. Preencha antes de incluí-la numa escala que
              trabalha nesses dias. A exceção de um dia se faz na grade.
            </FormHelperText>
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={testar} disabled={testando}>
            {testando ? "Testando..." : "Testar conexão"}
          </Button>
        </Stack>
        {teste && (
          <Alert severity={teste.ok ? "success" : "error"}>
            {teste.ok ? textoTesteOk(teste.mensagem, teste.filiais) : teste.mensagem}
          </Alert>
        )}
      </ConfigDropdownForm>

      <ConfigDropdownForm
        title="Mapa de lojas"
        subtitle="Liga cada loja à unidade correspondente no sistema externo. Loja sem ligação não é sincronizada."
        onSave={salvarMapa}
        loading={salvandoMapa}
        defaultOpen
      >
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={sugerir} disabled={sugerindo || !gravada.configurada}>
            {sugerindo ? "Buscando..." : "Sugerir pelo CNPJ"}
          </Button>
        </Stack>
        {lojas.length === 0 && <Alert severity="info">Não há lojas ativas cadastradas.</Alert>}
        {linhas.map((l, i) => {
          const loja = lojas.find((x) => x.id === l.loja_id);
          const atualizar = (parte: Partial<LinhaMapa>) =>
            setLinhas((ls) => ls.map((x, j) => (j === i ? { ...x, ...parte } : x)));
          return (
            <Stack key={l.loja_id} direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Loja" fullWidth disabled value={loja ? lojaLabel(loja) : l.loja_id} />
              <TextField
                label={rotuloEmpresa}
                fullWidth
                value={l.empresa}
                onChange={(e) => atualizar({ empresa: e.target.value })}
                slotProps={{ htmlInput: { maxLength: MAX_REF_INTEGRACAO } }}
              />
              <TextField
                label={rotuloFilial}
                fullWidth
                value={l.filial}
                onChange={(e) => atualizar({ filial: e.target.value })}
                slotProps={{ htmlInput: { maxLength: MAX_REF_INTEGRACAO } }}
              />
            </Stack>
          );
        })}
      </ConfigDropdownForm>
    </>
  );
}
